import { useContext, useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { SelectableCard } from '../../presentation-components/settings-components/data-import/selectable-card';
import { LoadingPage } from '../../pages/loading-page';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { SurveyTemplateService } from '../../../shared/services/survey-template/survey-template.service';
import { IPublishedTemplate } from '../../../shared/services/survey-template/survey-template.model';

interface Props {
  open: boolean;
  onClose: () => void;
  // Called with the new detached question group's id once a template has been instantiated.
  onInstantiated: (surveyQuestionGroupId: string) => void;
}

// Pick a published platform template and instantiate it into the caller's own account. The result
// is a fresh, editable question group; the template itself is untouched.
export const NewFromTemplateDialog = ({ open, onClose, onInstantiated }: Props) => {
  const service = new SurveyTemplateService();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [templates, setTemplates] = useState<IPublishedTemplate[]>();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setTemplates(undefined);
    service.listPublished()
      .then(setTemplates)
      .catch(() => setTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    if (!selectedId) return;
    setIsSubmitting(true);
    try {
      const result = await service.instantiate(selectedId);
      userNotificationsContext.openSnackbar({
        open: true,
        message: result.message,
        backgroundColor: result.unresolvedFactors.length > 0 ? 'text.primary' : 'primary.dark',
      });
      onInstantiated(result.surveyQuestionGroupId);
      onClose();
    } catch (err: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'An error occurred, please try again',
        backgroundColor: 'text.primary',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title='Start from a template'
      maxWidth='md'
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      submitButtonText='Use template'
      isSubmitting={isSubmitting}
      disableSaveButton={!selectedId}
    >
      <LoadingPage pageReady={!!templates}>
        {templates && templates.length === 0 && (
          <Typography variant='body2' sx={{ color: 'text.secondary', py: 2 }}>
            No templates are available yet.
          </Typography>
        )}
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
          Pick a ready-made survey to start from. It is copied into your account as a new question
          set that you can edit; the template stays as it is.
        </Typography>
        <Grid container spacing={2}>
          {(templates ?? []).map(t => (
            <Grid item xs={12} sm={6} key={t.id}>
              <SelectableCard
                title={t.name}
                description={`${t.description ? t.description + ' · ' : ''}${t.questionCount} question${t.questionCount === 1 ? '' : 's'}`}
                align='left'
                fullHeight
                selected={selectedId === t.id}
                onClick={() => setSelectedId(t.id)}
              />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mb: 1 }} />
      </LoadingPage>
    </DialogWrapper>
  );
};
